import {
  useLocations,
  useUpdateCameraGroup,
} from '@hakimo-ui/hakimo/data-access';
import { Floorplan } from '@hakimo-ui/hakimo/feature-shared';
import { CameraGroupDetail, CameraGroupPayload } from '@hakimo-ui/hakimo/types';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CameraGroupHeader from './CameraGroupHeader';
import CameraGroupFormCard from './cards/CameraGroupFormCard';
import CameraGroupSelectorCard from './cards/CameraGroupSelectorCard';

interface Props {
  group: CameraGroupDetail;
  onClose?: () => void;
}

function UpdateCameraGroup({ group, onClose }: Props) {
  const navigate = useNavigate();
  const [name, setName] = useState(group.name || '');
  const [description, setDescription] = useState(group.description || '');
  const [selectedCameraIds, setSelectedCameraIds] = useState<string[]>(
    group.cameras.map((c) => c.id)
  );
  const { data } = useLocations('');

  const locationOptions =
    data?.items.map((loc) => ({
      id: String(loc.id),
      name: loc.name,
    })) ?? [];

  const updateGroup = useUpdateCameraGroup(
    group.cameraGroupId,
    () => {
      toast('Camera group updated successfully!', { type: 'success' });
      navigate(`/cameras/camera-group?cameraGroupId=${group.cameraGroupId}`);
      onClose?.();
    },
    () => {
      toast('Camera Group update failed!', { type: 'error' });
    }
  );

  const onSubmit = () => {
    const payload: CameraGroupPayload = {
      name,
      description,
      locationId: String(group.locationId),
      cameras: selectedCameraIds,
    };

    updateGroup.mutate(payload);
  };

  return (
    <div className="bg-hakimo-light-bg dark:bg-hakimo-dark-bg min-h-screen">
      <CameraGroupHeader
        title={group.name}
        subtitle={group.locationName}
        actions={
          <>
            <Button
              variant="primary"
              onClick={onSubmit}
              loading={updateGroup.isLoading}
            >
              Save
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </>
        }
      />

      <div className="space-y-6 px-6 pt-4">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <CameraGroupFormCard
            mode="edit"
            name={name}
            description={description}
            locationId={String(group.locationId)}
            locationOptions={locationOptions}
            onDescriptionChange={setDescription}
            onNameChange={setName}
          />
          <Floorplan locationId={String(group.locationId)} />
        </div>

        <CameraGroupSelectorCard
          locationId={String(group.locationId)}
          value={selectedCameraIds}
          currentGroupId={group.cameraGroupId}
          onChange={setSelectedCameraIds}
        />
      </div>
    </div>
  );
}

export default UpdateCameraGroup;
